import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { FormControlValidators } from '../../../core/component/forms/form-control.validators';
import { LocalDate } from '../../../core/model/local-date';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { IncorrectPayment } from '../../model/incorrect-payment';
import { VoidPaymentModel } from '../../model/void-payment';
import { IncorrectPaymentService } from '../../service/data/incorrect-payment.service';
import { finalize, tap } from 'rxjs';

@Component({
    selector: 'app-void-payment-form',
    templateUrl: './void-payment-form.component.html',
    styleUrls: ['./void-payment-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VoidPaymentFormComponent implements OnInit {
  constructor(
    private readonly _actionService: ActionWindowService,
    private readonly _paymentService: IncorrectPaymentService,
    private readonly _fb: FormBuilder,
    private readonly _fcf: FormControlExFactory
  ) {}

  @Input() dataContext: IncorrectPayment;

  form: FormGroup;
  minDate: LocalDate | number;
  isSubmitting = false;

  get changeAccountingControl(): FormControlEx {
    return this.form.controls['changeAccounting'] as FormControlEx;
  }

  get transactionDateControl(): FormControlEx {
    return this.form.controls['verificationBusinessTransactionDate'] as FormControlEx;
  }

  get showAccountingClosedDate(): boolean {
    return this.changeAccountingControl.value && this.transactionDateControl.value <= this.minDate;
  }

  cancel() {
    this._actionService.close();
  }

  ngOnInit() {
    let adjusted = false;
    let transactionDate = this.dataContext.businessTransactionDate;
    let transactionDateValidator = null;

    if (this.dataContext.companyAccountingClosedToDate?.hasValue) {
      const date = this.dataContext.companyAccountingClosedToDate.clone();
      date.addDays(1); // (modifies the original instance - that's why we have to clone first...)
      this.minDate = date;
      transactionDateValidator = FormControlValidators.minLocalDate(date);

      if (transactionDate < this.minDate) {
        transactionDate = this.minDate;
        adjusted = true;
      }
    } else {
      this.minDate = Number.NaN;
    }

    this.form = this._fb.group({
      changeAccounting: this._fcf.create('INCORRECT_PAYMENT.VOID_CHANGE_ACCOUTING_DATE', adjusted),
      verificationBusinessTransactionDate: this._fcf.create(
        'ACCOUNT.ACCOUNTING_DATE',
        transactionDate,
        transactionDateValidator,
        null,
        'INCORRECT_PAYMENT.HELP_TEXT.VOID_ACCOUNTING_DATE'
      ),
      verificationDescription: this._fcf.create('ACCOUNT_TRANSACTION.DESCRIPTION', null),
    });
  }

  onSubmit() {
    if (this.form.invalid || this.isSubmitting) return;

    this.isSubmitting = true;

    const model: VoidPaymentModel = {
      paymentId: this.dataContext.id,
      ...this.form.value,
    };

    this._paymentService
      .voidPayment(model)
      .pipe(
        tap(payment => this._actionService.complete(payment)),
        finalize(() => (this.isSubmitting = false))
      )
      .subscribe();
  }
}
